import { CAlert } from "@coreui/react-pro";
import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { AggSearchProvider } from "../contexts/agg-search";
import { GeoJSONDataProvider } from "../contexts/geojson-data";
import { SearchProvider } from "../contexts/search-context";
// AcceptInvite is also eager-loaded so the OTT redirect fires from
// a bundle that's already resolved: any additional load hop between
// the invitation email click and Logto's signIn() call risks
// interacting badly with clearTokens: false and existing sessions.
import AcceptInvite from "../Pages/auth/AcceptInvite";
// Callback is eager-loaded: it owns the OIDC code-exchange and runs at
// /callback?code=…. If lazyWithReload tripped here, the page reload
// would wipe the SDK's mid-flight PKCE state and the auth code in the
// URL would be left partially consumed, the next attempt fails until
// the user clicks "Sign in" a second time with a fresh code. Keeping
// Callback in the main bundle costs a few KB but guarantees the code
// exchange completes in a single tab navigation.
import Callback from "../Pages/auth/Callback";
import ManageDynamicLinks from "../Pages/CRM/DynamicLink/ManageDynamicLinks";
import ViewLinkDefinition from "../Pages/CRM/DynamicLink/ViewLinkDefinition";
import Email from "../Pages/login/Email";
import LogTo from "../Pages/login/LogTo";
import { lazyWithReload } from "../tools/lazy-with-reload";

const Account = lazyWithReload(() => import("../Pages/Account/Account"));
const BaseLayout = lazyWithReload(() => import("../layout/BaseLayout"));
const ConfigureSubscription = lazyWithReload(
  () => import("../Pages/Account/ConfigureSubscription"),
);
const CreateCustomLocation = lazyWithReload(
  () => import("../Pages/CRM/CustomLocations/CreateCustomLocation"),
);
const CreateList = lazyWithReload(() => import("../components/MyLists/CreateList"));
const CreateSearch = lazyWithReload(() => import("../components/Manage/Searches/CreateSearch"));
const CRM = lazyWithReload(() => import("../Pages/CRM"));
const CRMContent = lazyWithReload(() => import("../components/CRM/Components/CRMContent"));
const CrmProductEngagement = lazyWithReload(
  () => import("../components/CRM/Components/CrmProductEngagement"),
);
const CrmSales = lazyWithReload(() => import("../components/CRM/Components/CrmSales"));
const CrmTraining = lazyWithReload(() => import("../components/CRM/Components/CrmTraining"));
const CustomLocations = lazyWithReload(
  () => import("../Pages/CRM/CustomLocations/CustomLocations"),
);
const Dashboard = lazyWithReload(() => import("../components/Dashboard"));
const DataAgg = lazyWithReload(() => import("../components/Planning/Components/DataAgg"));
const DataAnalytics = lazyWithReload(() => import("../Pages/DataAnalytics/DataAnalytics"));
const DefaultLayout = lazyWithReload(() => import("../layout/DefaultLayout"));
const EditList = lazyWithReload(() => import("../components/MyLists/EditList"));
const EditSearch = lazyWithReload(() => import("../components/Manage/Searches/EditSearch"));
const Glossary = lazyWithReload(() => import("../components/Glossary"));
const Licenses = lazyWithReload(() => import("../Pages/Licenses"));
const LocationDetail = lazyWithReload(() => import("../components/ClinicDetail"));
const LocationNotes = lazyWithReload(
  () => import("../components/ClinicDetail/Components/LocationNotes"),
);
const Login = lazyWithReload(() => import("../Pages/login/Login"));
const MinimalLayout = lazyWithReload(() => import("../layout/MinimalLayout"));
const MonthlySales = lazyWithReload(() => import("../Pages/DataAnalytics/MonthlySales"));
const MyLists = lazyWithReload(() => import("../components/MyLists"));
const Overview = lazyWithReload(() => import("../components/ClinicDetail/Overview"));
const Page404 = lazyWithReload(() => import("../Pages/page404/Page404"));
const Permissions = lazyWithReload(() => import("../components/Permissions"));
const Planning = lazyWithReload(() => import("../components/Planning"));
const PlanningHome = lazyWithReload(() => import("../components/Planning/PlanningHome"));
const Products = lazyWithReload(() => import("../components/Glossary/Components/Products"));
const Profile = lazyWithReload(() => import("../Pages/Account/Profile"));
const RealTimeCallDash = lazyWithReload(() => import("../Pages/DataAnalytics/RealTimeCallDash"));
const Searches = lazyWithReload(() => import("../components/Manage/Searches"));
const SubscriptionOverview = lazyWithReload(() => import("../Pages/Account/SubscriptionOverview"));
const TabularDataDash = lazyWithReload(() => import("../Pages/DataAnalytics/TabularDataDash"));
const Territories = lazyWithReload(() => import("../Pages/Account/Territories"));
const TerritoryBuilder = lazyWithReload(
  () => import("../components/Planning/Components/TerritoryBuilder"),
);
const TrainingCourseLayout = lazyWithReload(
  () => import("../components/CRM/Components/TrainingCourse/TrainingCourseLayout"),
);
const TrainingCourses = lazyWithReload(
  () => import("../Pages/CRM/TrainingCourses/TrainingCourses"),
);
const Treatments = lazyWithReload(() => import("../components/Glossary/Components/Treatments"));
const UserCreate = lazyWithReload(() => import("../components/Manage/Users/Create"));
const UserEdit = lazyWithReload(() => import("../components/Manage/Users/Edit"));
const Users = lazyWithReload(() => import("../components/Manage/Users"));
const ViewCustomLocation = lazyWithReload(
  () => import("../Pages/CRM/CustomLocations/ViewCustomLocation"),
);
const ViewList = lazyWithReload(() => import("../components/MyLists/ViewList"));
const ViewTrainingCourse = lazyWithReload(
  () => import("../Pages/CRM/TrainingCourses/ViewTrainingCourse"),
);
const ViewTrainingCourseSession = lazyWithReload(
  () => import("../Pages/CRM/TrainingCourses/ViewTrainingCourseSession"),
);
const WithAuth = lazyWithReload(() => import("../components/Auth/with-auth"));
const CrmProducts = lazyWithReload(() => import("../Pages/CRM/Products/CrmProducts"));
const CrmProductCategories = lazyWithReload(
  () => import("../Pages/CRM/Products/CrmProductCategories"),
);
const ClientLocations = lazyWithReload(
  () => import("../Pages/CRM/ClientLocations/ClientLocations"),
);
const location = {
  path: "location",
  name: "Location",
  children: [
    {
      path: ":locationId",
      name: "View Location",
      element: <LocationDetail />,
      children: [
        {
          index: true,
          element: <Overview />,
        },
        {
          path: "crm",
          element: <CRMContent />,
        },
        {
          path: "sales",
          element: <CrmSales />,
        },
        {
          path: "training",
          element: <CrmTraining />,
        },
        {
          path: "notes",
          element: <LocationNotes />,
        },
        {
          path: "product-engagement",
          element: <CrmProductEngagement />,
        },
      ],
      // exact: true,
      handle: {
        crumb: (data) => {
          return <>View Location </>;
        },
      },
    },
  ],
};

const clinic = {
  ...location,
  path: "clinic",
};

const routes = [
  {
    name: "Home",
    element: (
      <WithAuth>
        <GeoJSONDataProvider>
          <AggSearchProvider>
            <BaseLayout />
          </AggSearchProvider>
        </GeoJSONDataProvider>
      </WithAuth>
    ),
    children: [
      {
        name: "withDefaultLayout",
        element: <DefaultLayout />,
        children: [
          {
            path: "/",
            name: "Dashboard",
            handle: {
              crumb: (data) => {
                return "Filter Locations";
              },
            },

            children: [
              {
                index: true,

                element: (
                  <SearchProvider>
                    <Dashboard />
                  </SearchProvider>
                ),
              },
              {
                name: "static pages",

                children: [
                  {
                    name: "License Information",
                    path: "licenses",
                    element: <Licenses />,
                  },
                ],
              },
              {
                children: [location, clinic],
              },
            ],
          },
          {
            path: "myaccount",
            exact: true,
            element: <Navigate to={"/account/profile"} />,
          },
          {
            path: "account",
            name: "My Account",
            element: <Account />,
            children: [
              {
                index: true,
                element: <Profile />,
              },
              {
                path: "subscription",
                handle: {
                  crumb: () => "Subscription",
                },
                children: [
                  {
                    index: true,
                    element: <SubscriptionOverview />,
                  },
                  {
                    path: "settings",
                    element: <ConfigureSubscription />,
                    handle: {
                      crumb: () => "Configure",
                    },
                  },
                ],
              },
              {
                path: "territories",
                element: <Territories />,
                handle: {
                  crumb: () => "Territories",
                },
              },
            ],
            handle: { crumb: () => "Account" },
          },
          {
            path: "/glossary",
            name: "Glossary",
            handle: {
              crumb: () => "Glossary",
            },
            children: [
              { index: true, name: "Glossary Index", element: <Glossary /> },
              {
                path: "products",
                name: "Products",
                element: <Products />,
                exact: true,
                handle: {
                  crumb: (data) => {
                    return "Product Catalogue";
                  },
                },
              },
              {
                path: "treatments",
                name: "Treaments",
                element: <Treatments />,
                exact: true,
                handle: {
                  crumb: (data) => {
                    return "Treatment Catalogue";
                  },
                },
              },
            ],
          },
          {
            path: "data-analytics",
            name: "Data and Analytics",
            element: <DataAnalytics />,
            handle: {
              crumb: (data) => "Data and Analytics",
            },
            children: [
              {
                index: true,
                element: (
                  <Permissions
                    roles={["data_analytics_live", "data_analytics_monthly"]}
                    overlay={<Navigate to="tabular-data" replace />}
                  >
                    <Navigate to="live-dashboard" replace />
                  </Permissions>
                ),
              },
              {
                // element: <AnalyticsLiveDash />,
                path: "live-dashboard",
                children: [
                  {
                    index: true,
                    element: <RealTimeCallDash />,
                  },
                  {
                    path: "call-stats",
                    element: <CAlert color={"info"}>Coming Soon...</CAlert>,
                  },
                  {
                    path: "sales-stats",
                    element: <CAlert color={"info"}>Coming Soon...</CAlert>,
                  },
                ],
              },
              {
                path: "monthly-report",
                children: [
                  {
                    index: true,
                    element: <MonthlySales />,
                  },
                  {
                    path: "comex-reporting",
                    element: <CAlert color={"info"}>Coming Soon...</CAlert>,
                  },
                  {
                    path: "rep-reporting",
                    element: <CAlert color={"info"}>Coming Soon...</CAlert>,
                  },
                ],
              },
              {
                path: "tabular-data",
                children: [
                  {
                    index: true,
                    element: <TabularDataDash />,
                  },
                ],
              },
            ],
          },
          {
            path: "lists",
            name: "My Lists",
            handle: {
              crumb: (data) => {
                return "Lists";
              },
            },
            children: [
              {
                index: true,
                element: <MyLists listType={"user"} />,
              },
              location,
              clinic,
              {
                handle: {
                  crumb: (data) => {
                    return "Standard Lists";
                  },
                },
                path: "standard",
                children: [
                  { index: true, element: <MyLists listType={"standard"} /> },
                  {
                    path: "view/:listId",
                    name: "View List",
                    // element: <ViewList/>,
                    handle: {
                      crumb: (data) => "View List",
                    },
                    children: [{ index: true, element: <ViewList /> }, location, clinic],
                  },
                ],
              },
              {
                path: "view/:listId",
                name: "View List",
                handle: {
                  crumb: (data) => "View List",
                },
                children: [{ index: true, element: <ViewList /> }, location, clinic],
              },
              {
                path: "edit/:listId",
                name: "Edit List",
                element: <EditList />,
                handle: {
                  crumb: (data) => "Edit List",
                },
              },
              {
                path: "create",
                name: "Create List",
                element: <CreateList />,
                handle: {
                  crumb: (data) => "Create List",
                },
              },
            ],
          },
          {
            name: "CRM",
            path: "crm",
            // handle: {
            //   crumb: (data) => "CRM",
            // },
            children: [
              {
                index: true,
                element: <CRM />,
              },
              {
                path: "client-locations",
                element: <ClientLocations />,
              },
              {
                path: "custom-locations",
                children: [
                  {
                    index: true,
                    element: <CustomLocations />,
                  },
                  {
                    path: "new",
                    element: <CreateCustomLocation />,
                  },
                  {
                    path: "view/:locationId",
                    element: <ViewCustomLocation />,
                  },
                ],
              },
              {
                path: "training-courses",
                element: <TrainingCourseLayout />,
                handle: {
                  crumb: (data) => "Training Courses",
                },
                children: [
                  {
                    index: true,
                    element: <TrainingCourses />,
                  },
                  {
                    name: "View Course",
                    path: ":courseId",
                    handle: {
                      crumb: (data) => "View Module",
                    },
                    children: [
                      {
                        index: true,
                        element: <ViewTrainingCourse />,
                      },
                      {
                        path: "edit",
                        element: <>EDIT</>,
                      },
                      {
                        path: "session",
                        children: [
                          {
                            path: ":sessionId",
                            element: <ViewTrainingCourseSession />,
                            handle: {
                              crumb: (data) => "View Session",
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "dynamic-links",
                handle: {
                  crumb: () => "Dynamic Links",
                },
                children: [
                  {
                    index: true,
                    element: <ManageDynamicLinks />,
                  },
                  {
                    path: "view/:linkDefinitionId",
                    element: <ViewLinkDefinition />,
                  },
                ],
              },
              {
                path: "products",
                handle: {
                  crumb: () => "Products",
                },
                children: [
                  {
                    index: true,
                    element: <CrmProducts />,
                  },
                  {
                    path: "categories",
                    element: <CrmProductCategories />,
                    handle: {
                      crumb: () => "Categories",
                    },
                  },
                ],
              },
            ],
          },
          {
            path: "manage",
            name: "Management",
            element: (
              <React.Fragment>
                <Outlet />
              </React.Fragment>
            ),
            exact: true,
            handle: {
              crumb: (data) => {
                return "Management";
              },
            },
            children: [
              {
                index: true,
                element: (
                  <>
                    <h3>Management Page Information</h3>
                  </>
                ),
              },
              {
                path: "users",
                exact: true,
                name: "Manage Users",
                handle: {
                  crumb: (data) => {
                    return "Users";
                  },
                },
                children: [
                  {
                    index: true,
                    element: <Users />,
                  },
                  {
                    name: "Create User",
                    path: "create",
                    element: <UserCreate />,
                    handle: {
                      crumb: (data) => {
                        return "Create Sub-User";
                      },
                    },
                  },
                  {
                    name: "Edit User",
                    path: "edit/:userId",
                    element: <UserEdit />,
                    handle: {
                      crumb: (data) => {
                        return "Edit Sub-User";
                      },
                    },
                  },
                ],
              },
              {
                path: "searches",
                exact: true,
                name: "Manage Searches",
                handle: {
                  crumb: (data) => {
                    return "Searches";
                  },
                },
                children: [
                  {
                    index: true,
                    element: <Searches />,
                  },
                  {
                    name: "Create Search",
                    path: "create",
                    element: <CreateSearch />,
                    handle: {
                      crumb: (data) => {
                        return "Create Search";
                      },
                    },
                  },
                  {
                    name: "Edit Search",
                    path: "edit/:searchId",
                    element: <EditSearch />,
                    handle: {
                      crumb: (data) => {
                        return "Edit Search";
                      },
                    },
                  },
                ],
              },
            ],
          },
          {
            path: "planning",
            element: <PlanningHome />,
            handle: {
              crumb: () => "Planning and Analysis",
            },
          },
        ],
      },

      {
        name: "withMinimalLayout",
        element: <MinimalLayout />,
        children: [
          {
            path: "planning",
            // name: "",
            // handle: {
            //   crumb: () => "Sales Targeting"
            // },
            element: <Planning />,
            // element: <SalesTargeting />,
            children: [
              {
                handle: {
                  crumb: () => "Territory Builder",
                },
                path: "territories",
                element: <TerritoryBuilder />,
              },
              {
                path: "regional-analysis",
                handle: {
                  crumb: () => "Analytics",
                },
                element: <DataAgg />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "login",
    name: "Login Page",
    element: <Login />,
    children: [
      {
        index: true,
        element: <LogTo />,
      },
      {
        path: "email",
        element: <Email />,
      },
    ],
  },
  // {
  //   // Self-signup entry. Redirects to Logto's hosted page on the
  //   // sign-up tab and lets logto-sync attach the user to the
  //   // restricted default subscription on first sign-in.
  //   path: 'register',
  //   name: 'Register Page',
  //   element: <Register />,
  //   exact: true,
  // },
  {
    // Logto OIDC callback. Completes the code exchange and routes the
    // user to wherever they were trying to reach.
    path: "callback",
    name: "Logto Callback",
    element: <Callback />,
    exact: true,
  },
  {
    // OTT invitation landing page. Reads `token` + `email` from the
    // query string and calls Logto SDK signIn with extraParams so the
    // hosted experience-side flow consumes the token, creates the
    // user on first acceptance, and redirects back to `/callback`.
    path: "accept-invite",
    name: "Accept Invitation",
    element: <AcceptInvite />,
    exact: true,
  },
  { path: "*", name: "Page 404", element: <Page404 /> },
];

export const getFlatItems = (items = [], parent = "") => {
  return items.flatMap((item) => {
    if (Object.hasOwn(item, "children")) {
      return [
        ...(item.title
          ? [
              {
                title: item.title,
                path: `${parent}/${item.path ?? ""}`.replace(/^\/+/, "/"),
              },
            ]
          : []),
      ];
    } else {
      if (Object.hasOwn(item, "path")) {
        return [
          {
            title: item.title,
            is: item.path,
            parent,
            path: `${parent}/${item.path ?? ""}`.replace(/^\/+/, "/"),
          },
        ];
      } else {
        return [];
      }
    }
  });
};

export const flatItemsToObject = () => {
  const obj = {};
  getFlatItems(routes, "")
    .filter((item) => !!item.title)
    .forEach((item) => {
      obj[item.path] = item.title;
    });

  return obj;
};

export default routes;
